'use client';

import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { sanitizeFirestoreData } from '@/lib/utils';
import type { JartinetHermandad, SocialPost } from '@/lib/types';
import { RichText } from '@/components/ui/rich-text';
import Image from '@/components/Image';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  Loader2, ArrowLeft, Church, Calendar, Users, Crown, BookOpen, MapPin, Sparkles, Newspaper, DoorOpen,
} from 'lucide-react';

function hexToRgba(hex: string, alpha: number) {
  const h = (hex || '#9b8854').replace('#', '');
  if (h.length < 6) return `rgba(155,136,84,${alpha})`;
  return `rgba(${parseInt(h.slice(0, 2), 16)},${parseInt(h.slice(2, 4), 16)},${parseInt(h.slice(4, 6), 16)},${alpha})`;
}

function StatCard({ icon, value, label, color }: { icon: React.ReactNode; value: string | number; label: string; color: string }) {
  return (
    <div className="rounded-2xl border border-white/8 bg-card/40 p-4 text-center">
      <div className="mx-auto mb-2 grid h-9 w-9 place-items-center rounded-xl" style={{ background: hexToRgba(color, 0.15), color }}>
        {icon}
      </div>
      <p className="text-lg font-black leading-none">{value}</p>
      <p className="mt-1 text-[0.65rem] uppercase tracking-wider text-muted-foreground">{label}</p>
    </div>
  );
}

export default function HermandadPublicaPage() {
  const { id } = useParams<{ id: string }>();
  const [hermandad, setHermandad] = useState<JartinetHermandad | null>(null);
  const [posts, setPosts] = useState<SocialPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const snap = await getDoc(doc(db, 'jartinet_hermandades', id));
        if (!cancelled) setHermandad(snap.exists() ? (sanitizeFirestoreData({ id: snap.id, ...snap.data() }) as JartinetHermandad) : null);
      } catch {
        if (!cancelled) setHermandad(null);
      }
      if (!cancelled) setLoading(false);

      // Publicaciones: igualdad + orden sobre el MISMO campo no vale, así que se
      // filtra por autor y se ordena en memoria (evita el índice compuesto).
      try {
        const ps = await getDocs(query(collection(db, 'social_posts'), where('authorId', '==', id), limit(30)));
        const lista = ps.docs
          .map((d) => sanitizeFirestoreData({ id: d.id, ...d.data() }) as SocialPost)
          .sort((a, b) => new Date(b.createdAt as any).getTime() - new Date(a.createdAt as any).getTime())
          .slice(0, 6);
        if (!cancelled) setPosts(lista);
      } catch {
        if (!cancelled) setPosts([]);
      }
    })();

    return () => { cancelled = true; };
  }, [id]);

  const color = hermandad?.colorPrimario || '#9b8854';
  const color2 = hermandad?.colorSecundario || '#000000';

  const fotos = useMemo(
    () => posts.flatMap((p) => (Array.isArray(p.mediaUrl) ? p.mediaUrl : p.mediaUrl ? [p.mediaUrl] : [])).filter(Boolean).slice(0, 8),
    [posts],
  );

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (!hermandad) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-4 text-center">
        <Church className="h-12 w-12 text-muted-foreground/50" />
        <h1 className="font-headline text-2xl font-bold">Esta hermandad no existe</h1>
        <Button asChild variant="outline"><Link to="/hermandades"><ArrowLeft className="h-4 w-4" /> Volver a las hermandades</Link></Button>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-background">
      {/* ── Hero ───────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        {hermandad.foto && (
          <Image src={hermandad.foto} alt="" className="absolute inset-0 h-full w-full scale-105 object-cover opacity-30" />
        )}
        <div className="absolute inset-0" style={{ background: `linear-gradient(to top, hsl(var(--background)), ${hexToRgba(color, 0.25)} 60%, transparent)` }} />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-background/20" />

        <div className="container relative mx-auto px-4 pb-12 pt-24 md:pt-28">
          <Button asChild variant="ghost" size="sm" className="mb-6 -ml-2 text-muted-foreground">
            <Link to="/hermandades"><ArrowLeft className="h-4 w-4" /> Todas las hermandades</Link>
          </Button>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            className="flex flex-col items-center gap-6 text-center md:flex-row md:items-end md:text-left"
          >
            {hermandad.escudoUrl && (
              <div className="relative flex-shrink-0">
                <div className="absolute inset-0 rounded-full blur-2xl" style={{ background: hexToRgba(color, 0.5) }} />
                <div className="relative grid h-32 w-32 place-items-center rounded-3xl border-2 bg-black/40 p-3 backdrop-blur md:h-40 md:w-40" style={{ borderColor: hexToRgba(color, 0.5) }}>
                  <img src={hermandad.escudoUrl} alt={hermandad.nombre} className="h-full w-full object-contain" />
                </div>
              </div>
            )}

            <div className="min-w-0 flex-1">
              {!!hermandad.titulos?.length && (
                <div className="mb-2 flex flex-wrap justify-center gap-1.5 md:justify-start">
                  {hermandad.titulos.map((t) => (
                    <span key={t} className="rounded-full border px-2.5 py-0.5 text-[0.65rem] font-bold uppercase tracking-wider" style={{ borderColor: hexToRgba(color, 0.4), background: hexToRgba(color, 0.12), color }}>
                      {t}
                    </span>
                  ))}
                </div>
              )}

              <h1 className="font-headline text-4xl font-black leading-tight md:text-6xl">{hermandad.nombre}</h1>
              {hermandad.nombreCompleto && hermandad.nombreCompleto !== hermandad.nombre && (
                <p className="mt-2 max-w-3xl text-sm text-muted-foreground md:text-base">{hermandad.nombreCompleto}</p>
              )}

              <div className="mt-4 flex flex-wrap justify-center gap-2 md:justify-start">
                <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold">{hermandad.tipo}</span>
                {hermandad.dia && (
                  <span className="flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-bold" style={{ borderColor: hexToRgba(color, 0.4), background: hexToRgba(color, 0.15), color }}>
                    <Calendar className="h-3 w-3" />
                    {hermandad.dia}
                  </span>
                )}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Filete bicolor */}
        <div className="h-1 w-full" style={{ background: `linear-gradient(90deg, ${color}, ${color2})` }} />
      </section>

      <div className="container mx-auto grid gap-8 px-4 py-12 lg:grid-cols-[1fr_20rem]">
        {/* ── Columna principal ───────────────────────────────────────────── */}
        <div className="space-y-8">
          {hermandad.titulares && (
            <section>
              <h2 className="mb-3 flex items-center gap-2 font-headline text-xl font-bold">
                <Sparkles className="h-5 w-5" style={{ color }} /> Titulares
              </h2>
              <p className="rounded-2xl border border-white/8 bg-card/40 p-5 text-sm leading-relaxed md:text-base">{hermandad.titulares}</p>
            </section>
          )}

          {(hermandad.bio || hermandad.info) && (
            <section>
              <h2 className="mb-3 flex items-center gap-2 font-headline text-xl font-bold">
                <BookOpen className="h-5 w-5" style={{ color }} /> Historia
              </h2>
              <div className="rounded-2xl border border-white/8 bg-card/40 p-5">
                <RichText>{hermandad.bio || hermandad.info || ''}</RichText>
              </div>
            </section>
          )}

          {fotos.length > 0 && (
            <section>
              <h2 className="mb-3 flex items-center gap-2 font-headline text-xl font-bold">
                <Church className="h-5 w-5" style={{ color }} /> Imágenes
              </h2>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {fotos.map((src, i) => (
                  <motion.div
                    key={`${src}-${i}`}
                    whileHover={{ y: -4 }}
                    className="aspect-square overflow-hidden rounded-xl border border-white/8"
                  >
                    <Image src={src} alt="" className="h-full w-full object-cover transition-transform duration-500 hover:scale-110" />
                  </motion.div>
                ))}
              </div>
            </section>
          )}

          {posts.length > 0 && (
            <section>
              <h2 className="mb-3 flex items-center gap-2 font-headline text-xl font-bold">
                <Newspaper className="h-5 w-5" style={{ color }} /> Últimas publicaciones
              </h2>
              <div className="space-y-3">
                {posts.slice(0, 4).map((p) => (
                  <article key={p.id} className="rounded-2xl border border-white/8 bg-card/40 p-4">
                    <div className="flex items-baseline justify-between gap-3">
                      <h3 className="font-bold">{p.title || 'Publicación'}</h3>
                      {p.createdAt && (
                        <time className="flex-shrink-0 text-[0.7rem] text-muted-foreground">
                          {format(new Date(p.createdAt as any), "d 'de' MMMM yyyy", { locale: es })}
                        </time>
                      )}
                    </div>
                    <div className="mt-1.5 line-clamp-4 text-sm text-muted-foreground">
                      <RichText>{p.content || ''}</RichText>
                    </div>
                  </article>
                ))}
              </div>
              <Button asChild variant="outline" className="mt-4 w-full">
                <Link to="/social">Ver todo en la red social</Link>
              </Button>
            </section>
          )}
        </div>

        {/* ── Lateral ─────────────────────────────────────────────────────── */}
        <aside className="space-y-4 lg:sticky lg:top-24 lg:self-start">
          <div className="grid grid-cols-3 gap-2">
            <StatCard icon={<Users className="h-4 w-4" />} value={(hermandad.hermanos || []).length} label="Hermanos" color={color} />
            <StatCard icon={<Crown className="h-4 w-4" />} value={(hermandad.hermanosMayores || []).length} label="H. Mayores" color={color} />
            <StatCard icon={<Church className="h-4 w-4" />} value={hermandad.fundacion || '—'} label="Fundación" color={color} />
          </div>

          {hermandad.sede && (
            <div className="rounded-2xl border border-white/8 bg-card/40 p-4">
              <p className="flex items-center gap-2 text-[0.65rem] font-bold uppercase tracking-widest text-muted-foreground">
                <MapPin className="h-3.5 w-3.5" style={{ color }} /> Sede canónica
              </p>
              <p className="mt-1.5 text-sm font-semibold">{hermandad.sede}</p>
            </div>
          )}

          {hermandad.libroReglasUrl && (
            <Button asChild variant="outline" className="w-full">
              <a href={hermandad.libroReglasUrl} target="_blank" rel="noopener noreferrer">
                <BookOpen className="h-4 w-4" /> Libro de reglas
              </a>
            </Button>
          )}

          <div className="relative overflow-hidden rounded-2xl border p-5 text-center" style={{ borderColor: hexToRgba(color, 0.35), background: `linear-gradient(135deg, ${hexToRgba(color, 0.18)}, transparent 70%)` }}>
            <p className="text-sm font-bold">¿Quieres ser hermano?</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Entra en Jartiland y solicita tu hermandad desde Jartinet.
            </p>
            <Button asChild className="mt-3 w-full">
              <Link to="/apply"><DoorOpen className="h-4 w-4" /> Entrar al servidor</Link>
            </Button>
          </div>
        </aside>
      </div>
    </main>
  );
}
